import { Disposable, ICommandService, Inject, Injector } from '@univerjs/core';
import { ComponentManager, IMenuManagerService, RibbonOthersGroup, RibbonStartGroup } from '@univerjs/ui';

import {
  InsertFormulaOperation, InsertLabelOperation, InsertTextOperation,
  InsertNumberOperation, InsertDateTimeOperation, InsertLongLabelOperation,
  InsertLongTextOperation, InsertHeaderOperation, InsertHeaderTextOperation,
  InsertValueOperation, InsertCompanyOperation, InsertCountryOperation,
  InsertCurrencyOperation, InsertCompanyDropdownOperation, InsertAccountCodeOperation, MergeOperation, DeleteComponentOperation,
} from './operation';
import {
  LabelIcon, TextIcon, FormulaIcon, NumberIcon, DateTimeIcon, LongLabelIcon,
  LongTextIcon, HeaderIcon, HeaderTextIcon, ValueIcon, CompanyIcon, CountryIcon,
  CurrencyIcon, CompanyDropdownIcon, InsertAccountCodeIcon, MergeIcon, DeleteComponentIcon,
} from './icon';
import {
  InsertLabelButtonFactory, InsertTextButtonFactory, InsertFormulaButtonFactory,
  InsertNumberButtonFactory, InsertDateTimeButtonFactory, InsertLongLabelButtonFactory,
  InsertLongTextButtonFactory, InsertHeaderButtonFactory, InsertHeaderTextButtonFactory,
  InsertValueButtonFactory, InsertCompanyButtonFactory, InsertCountryButtonFactory,
  InsertCurrencyButtonFactory, InsertCompanyDropdownButtonFactory, InsertAccountCodeButtonFactory, MergeButtonFactory, DeleteComponentButtonFactory,
} from './menu';

type AnyCommand = { id: string };
type AnyIconFactory = (...args: any[]) => any;
type AnyMenuFactory = (...args: any[]) => any;

interface ComponentMenuEntry {
  operation: AnyCommand;
  iconName: string;
  icon: AnyIconFactory;
  menuFactory: AnyMenuFactory;
  // Merge claims the slot Univer's merge dropdown had; everything else defaults to OTHERS.
  ribbon?: { group: string; order: number };
}

const ENTRIES: ComponentMenuEntry[] = [
  { operation: InsertLabelOperation,       iconName: 'LabelIcon',             icon: LabelIcon,             menuFactory: InsertLabelButtonFactory },
  { operation: InsertTextOperation,        iconName: 'TextIcon',              icon: TextIcon,              menuFactory: InsertTextButtonFactory },
  { operation: InsertFormulaOperation,     iconName: 'FormulaIcon',           icon: FormulaIcon,           menuFactory: InsertFormulaButtonFactory },
  { operation: InsertNumberOperation,      iconName: 'NumberIcon',            icon: NumberIcon,            menuFactory: InsertNumberButtonFactory },
  { operation: InsertDateTimeOperation,    iconName: 'DateTimeIcon',          icon: DateTimeIcon,          menuFactory: InsertDateTimeButtonFactory },
  { operation: InsertLongLabelOperation,   iconName: 'LongLabelIcon',         icon: LongLabelIcon,         menuFactory: InsertLongLabelButtonFactory },
  { operation: InsertLongTextOperation,    iconName: 'LongTextIcon',          icon: LongTextIcon,          menuFactory: InsertLongTextButtonFactory },
  { operation: InsertHeaderOperation,      iconName: 'HeaderIcon',            icon: HeaderIcon,            menuFactory: InsertHeaderButtonFactory },
  { operation: InsertHeaderTextOperation,  iconName: 'HeaderTextIcon',        icon: HeaderTextIcon,        menuFactory: InsertHeaderTextButtonFactory },
  { operation: InsertValueOperation,       iconName: 'ValueIcon',             icon: ValueIcon,             menuFactory: InsertValueButtonFactory },
  { operation: InsertCompanyOperation,     iconName: 'CompanyIcon',           icon: CompanyIcon,           menuFactory: InsertCompanyButtonFactory },
  { operation: InsertCountryOperation,     iconName: 'CountryIcon',           icon: CountryIcon,           menuFactory: InsertCountryButtonFactory },
  { operation: InsertCurrencyOperation,    iconName: 'CurrencyIcon',          icon: CurrencyIcon,          menuFactory: InsertCurrencyButtonFactory },
  { operation: InsertCompanyDropdownOperation, iconName: 'CompanyDropdownIcon', icon: CompanyDropdownIcon,   menuFactory: InsertCompanyDropdownButtonFactory },
  { operation: InsertAccountCodeOperation, iconName: 'InsertAccountCodeIcon', icon: InsertAccountCodeIcon, menuFactory: InsertAccountCodeButtonFactory },
  { operation: MergeOperation,             iconName: 'MergeIcon',             icon: MergeIcon,             menuFactory: MergeButtonFactory, ribbon: { group: RibbonStartGroup.LAYOUT, order: 8 } },
  { operation: DeleteComponentOperation,   iconName: 'DeleteComponentIcon',   icon: DeleteComponentIcon,   menuFactory: DeleteComponentButtonFactory },
];

/** Registers commands, icons and menu entries for the custom component toolbar. */
export class ComponentButtonsController extends Disposable {
  constructor(
    @Inject(Injector) private readonly _injector: Injector,
    @ICommandService private readonly _commandService: ICommandService,
    @IMenuManagerService private readonly _menuManagerService: IMenuManagerService,
    @Inject(ComponentManager) private readonly _componentManager: ComponentManager,
  ) {
    super();

    for (const entry of ENTRIES) {
      this.disposeWithMe(this._commandService.registerCommand(entry.operation as any));
      this.disposeWithMe(this._componentManager.register(entry.iconName, entry.icon));
    }

    const menu: Record<string, Record<string, any>> = {};
    for (const entry of ENTRIES) {
      const group = entry.ribbon?.group ?? RibbonOthersGroup.OTHERS;
      if (!menu[group]) menu[group] = {};
      menu[group][entry.operation.id] = {
        order: entry.ribbon?.order ?? 10,
        menuItemFactory: entry.menuFactory,
      };
    }
    this._menuManagerService.mergeMenu(menu);
  }
}
